'use client';

import { useState, useEffect } from 'react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { X, Settings, Check } from 'lucide-react';

export default function CookieBanner() {
  const {
    consent,
    isOpen,
    showSettings,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    closeBanner,
  } = useCookieConsent();

  const [preferences, setPreferences] = useState({
    essential: true,
    analytical: false,
    marketing: false,
  });

  // Initialize preferences from existing consent if available
  useEffect(() => {
    if (consent) {
      setPreferences({
        essential: true,
        analytical: consent.analytical,
        marketing: consent.marketing,
      });
    }
  }, [consent]);

  // Don't render if consent is already given and banner is closed
  if (!isOpen && consent) {
    return null;
  }

  return (
    <>
      {/* Main Banner - Minimalist Bar */}
      {isOpen && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border border-white/20 dark:border-slate-800/50 rounded-2xl shadow-xl p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <p className="text-sm md:text-base text-slate-700 dark:text-slate-300 flex-1">
                  We use cookies to improve your experience. By continuing, you agree to our use of cookies.
                </p>
                <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto">
                  <button
                    onClick={rejectAll}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white/50 dark:bg-slate-800/50 hover:bg-white/70 dark:hover:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={openSettings}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white/50 dark:bg-slate-800/50 hover:bg-white/70 dark:hover:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-2"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-lg shadow-blue-600/30"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {isOpen && showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-white/20 dark:border-slate-800/50 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Cookie Preferences
                </h2>
                <button
                  onClick={closeBanner}
                  className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Description */}
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                Manage your cookie preferences. You can enable or disable different types of cookies below.
              </p>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                        Essential Cookies
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        These cookies are necessary for the website to function properly. They cannot be disabled.
                      </p>
                    </div>
                    <div className="ml-4 flex items-center">
                      <div className="w-12 h-6 bg-blue-600 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytical Cookies */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                        Analytical Cookies
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously (e.g., Google Analytics).
                      </p>
                    </div>
                    <div className="ml-4 flex items-center">
                      <button
                        type="button"
                        onClick={() => setPreferences({ ...preferences, analytical: !preferences.analytical })}
                        className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          preferences.analytical ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                        role="switch"
                        aria-checked={preferences.analytical}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.analytical ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                        Marketing Cookies
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        These cookies are used to track visitors across websites to display relevant advertisements (tracking pixels).
                      </p>
                    </div>
                    <div className="ml-4 flex items-center">
                      <button
                        type="button"
                        onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                        className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          preferences.marketing ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                        role="switch"
                        aria-checked={preferences.marketing}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={closeBanner}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white/50 dark:bg-slate-800/50 hover:bg-white/70 dark:hover:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => savePreferences(preferences)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
                >
                  <Check size={16} />
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


